import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest } from '@adflow/frontend/util-query';
import type {
	AddColumnTemplateDto,
	DataTableItemType,
	EditColumnTemplateDto,
} from '@adflow/shared/util-column-templates';
import {
	AddColumnTemplate,
	DeleteColumnTemplate,
	EditColumnTemplate,
	GetColumnTemplate,
	GetDefaultColumnTemplate,
	ListColumnTemplates,
} from '@adflow/shared/util-column-templates';

const columnTemplateKeys = {
	all: ['column-template'] as const,

	allType: (type: DataTableItemType) =>
		[...columnTemplateKeys.all, type] as const,

	list: (type: DataTableItemType) =>
		[...columnTemplateKeys.allType(type)] as const,

	view: (templateId?: number) =>
		[...columnTemplateKeys.all, templateId] as const,

	viewDefault: (type: DataTableItemType) =>
		[...columnTemplateKeys.allType(type), 'default'] as const,
};

export function useColumnTemplatesQuery({ type }: { type: DataTableItemType }) {
	return useQuery({
		queryKey: columnTemplateKeys.list(type),
		queryFn: ({ signal }) =>
			apiRequest(
				ListColumnTemplates,
				{ query: { columnTemplateType: type } },
				{ signal }
			),
	});
}

export interface UseColumnTemplateQueryArgs {
	templateId?: number;
	enabled?: boolean;
}
export function useColumnTemplateQuery({
	templateId,
	enabled = true,
}: UseColumnTemplateQueryArgs) {
	return useQuery({
		queryKey: columnTemplateKeys.view(templateId),
		queryFn: ({ signal }) =>
			apiRequest(
				GetColumnTemplate,
				{ params: { columnTemplateId: templateId ?? 0 } },
				{ signal }
			),
		enabled: enabled && Boolean(templateId),
	});
}

export interface UseDefaultColumnTemplateQueryArgs {
	type: DataTableItemType;
	enabled?: boolean;
}
export function useDefaultColumnTemplateQuery({
	type,
	enabled = true,
}: UseDefaultColumnTemplateQueryArgs) {
	return useQuery({
		queryKey: columnTemplateKeys.viewDefault(type),
		queryFn: async ({ signal, client: queryClient }) => {
			const defaultColumnTemplate = await apiRequest(
				GetDefaultColumnTemplate,
				{ query: { columnTemplateType: type } },
				{ signal }
			);

			if (defaultColumnTemplate) {
				queryClient.setQueryData(
					columnTemplateKeys.view(defaultColumnTemplate.id),
					defaultColumnTemplate
				);
			}

			return defaultColumnTemplate;
		},
		enabled: enabled && Boolean(type),
	});
}

export function useAddColumnTemplateMutation(type: DataTableItemType) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: AddColumnTemplateDto) =>
			apiRequest(AddColumnTemplate, { body }),
		onSuccess: (resp) => {
			void queryClient.setQueryData(columnTemplateKeys.view(resp.id), resp);

			void queryClient.invalidateQueries({
				queryKey: columnTemplateKeys.allType(type),
			});

			if (resp.savedDefault) {
				void queryClient.invalidateQueries({
					queryKey: columnTemplateKeys.viewDefault(type),
				});
			}
		},
	});
}

interface EditColumnTemplateParams {
	id: number;
	body: EditColumnTemplateDto;
}

export function useEditColumnTemplateMutation(type: DataTableItemType) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, body }: EditColumnTemplateParams) =>
			apiRequest(EditColumnTemplate, {
				params: { columnTemplateId: id },
				body,
			}),
		onSuccess: (resp) => {
			void queryClient.setQueryData(columnTemplateKeys.view(resp.id), resp);

			void queryClient.invalidateQueries({
				queryKey: columnTemplateKeys.allType(type),
			});

			if (resp.savedDefault) {
				void queryClient.invalidateQueries({
					queryKey: columnTemplateKeys.viewDefault(type),
				});
			}
		},
	});
}

interface DeleteColumnTemplateParams {
	id: number;
}

export function useColumnTemplateDelete() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id }: DeleteColumnTemplateParams) =>
			apiRequest(DeleteColumnTemplate, { params: { columnTemplateId: id } }),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: columnTemplateKeys.all });
		},
	});
}
